import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";
import { useDateFilter, getRangeLabel } from "../../contexts/DateFilterContext";
import { useDateRangeDraft } from "./useDateRangeDraft";
import PresetList from "./PresetList";
import DualMonthCalendar from "./DualMonthCalendar";

const ghostBtnStyle = {
	padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
	border: "1px solid var(--color-border)", background: "transparent",
	color: "var(--color-text-light)", cursor: "pointer",
};
const primaryBtnStyle = {
	padding: "6px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700,
	border: "none", background: "#6f42c1", color: "#fff", cursor: "pointer",
};

const GlobalDateRangePicker = () => {
	const { globalRange, globalCustomRange, setGlobalRange } = useDateFilter();
	const [open, setOpen] = useState(false);
	const containerRef = useRef(null);

	const draft = useDateRangeDraft(globalRange, globalCustomRange);

	useEffect(() => {
		if (!open) return;
		const handleOutside = (e) => { if (!containerRef.current?.contains(e.target)) setOpen(false); };
		const handleEscape = (e) => { if (e.key === "Escape") setOpen(false); };
		document.addEventListener("mousedown", handleOutside);
		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("mousedown", handleOutside);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [open]);

	const openDropdown = () => { draft.reset(); setOpen(true); };

	const handleApply = () => {
		setGlobalRange(...draft.toCommitArgs());
		setOpen(false);
	};

	return (
		<div ref={containerRef} style={{ position: "relative", display: "inline-block", flexShrink: 0 }}>
			<button
				onClick={() => (open ? setOpen(false) : openDropdown())}
				style={{
					display: "flex", alignItems: "center", gap: 6, flexShrink: 0, whiteSpace: "nowrap",
					padding: "4px 11px", borderRadius: 20, fontSize: 11, fontWeight: 600,
					border: `1px solid ${open ? "#6f42c1" : "var(--color-border)"}`,
					background: open ? "rgba(111,66,193,0.1)" : "transparent",
					color: "var(--color-text-light)", cursor: "pointer",
				}}
			>
				<span style={{ width: 7, height: 7, borderRadius: "50%", background: "#6f42c1", flexShrink: 0 }} />
				<span style={{ whiteSpace: "nowrap" }}>Global: {getRangeLabel(globalRange, globalCustomRange)}</span>
				<FaChevronDown size={9} style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
			</button>

			<AnimatePresence>
				{open && (
					<motion.div
						initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
						transition={{ duration: 0.15 }}
						style={{
							position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 1000,
							background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
							borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.18)", overflow: "hidden",
						}}
					>
						<div style={{ display: "flex" }}>
							<PresetList activeKey={draft.draftKey} onSelect={draft.selectPreset} />
							<DualMonthCalendar rangeStart={draft.draftStart} rangeEnd={draft.draftEnd} onSelectDay={draft.selectDay} />
						</div>
						<div style={{
							display: "flex", justifyContent: "flex-end", gap: 8,
							padding: "10px 16px", borderTop: "1px solid var(--color-border)",
						}}>
							<button onClick={() => setOpen(false)} style={ghostBtnStyle}>Cancel</button>
							<button onClick={handleApply} disabled={!draft.isValid} style={{ ...primaryBtnStyle, opacity: draft.isValid ? 1 : 0.5 }}>
								Apply
							</button>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default GlobalDateRangePicker;
